/**
 * Fleet sub-tables — CyclesTable + AccountRidesTable.
 *
 * Extracted from FleetPanel.jsx (Phase 2 P2 decomposition · 2026-06).
 * Both are read-only presentational components driven entirely by their
 * props; no API calls of their own. Behaviour byte-identical to the
 * inlined versions (same testids, same number formatting, same colour
 * thresholds for positive/negative net earnings).
 */
import React from "react";
import { Briefcase } from "lucide-react";
import { useFinancialPrivacy } from "../../../lib/financialPrivacy";

export const AccountRidesTable = ({ rides }) => {
  const { mask } = useFinancialPrivacy();
  if (!rides || rides.length === 0) {
    return <div className="text-center py-12 text-sm text-muted-foreground">No account rides yet. Flag a passenger as a corporate account to start.</div>;
  }
  // Group by company
  const grouped = rides.reduce((acc, r) => {
    const k = r.account_company || "Unassigned";
    if (!acc[k]) acc[k] = { rides: [], net: 0, vat: 0, gross: 0 };
    acc[k].rides.push(r);
    const b = r.fare_breakdown;
    if (b) {
      acc[k].net += b.subtotal_pre_vat_net || 0;
      acc[k].vat += b.vat_amount || 0;
      acc[k].gross += b.gross || 0;
    } else {
      acc[k].gross += r.fare || 0;
    }
    return acc;
  }, {});
  return (
    <div data-testid="account-rides-table" className="space-y-4">
      {Object.entries(grouped).map(([company, data]) => (
        <div key={company} className="border border-border" data-testid={`account-group-${company}`}>
          <div className="bg-[#FAFAFB] border-b border-border px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Briefcase className="w-4 h-4 text-[#002FA7]" />
              <div className="font-bold truncate">{company}</div>
              <span className="label-mini text-muted-foreground">{data.rides.length} rides</span>
            </div>
            <div className="font-mono-tab text-xs flex items-center gap-3">
              <span className="text-muted-foreground">Net {mask(`£${data.net.toFixed(2)}`)}</span>
              <span className="text-muted-foreground">VAT {mask(`£${data.vat.toFixed(2)}`)}</span>
              <span className="font-bold text-[#002FA7]">Gross {mask(`£${data.gross.toFixed(2)}`)}</span>
            </div>
          </div>
          <div className="divide-y divide-border">
            {data.rides.slice(0, 50).map((r) => (
              <div key={r.id} className="px-3 py-2 flex items-center justify-between text-xs" data-testid={`account-ride-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{r.passenger_name} · {r.pickup_address} → {r.dropoff_address}</div>
                  <div className="label-mini text-muted-foreground font-mono-tab">{(r.completed_at || "").substring(0, 10)}</div>
                </div>
                <div className="font-mono-tab font-bold flex-shrink-0">{mask(`£${(r.fare || 0).toFixed(2)}`)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const CyclesTable = ({ cycles }) => {
  const { mask } = useFinancialPrivacy();
  if (!cycles || cycles.length === 0) {
    return <div className="text-center py-12 text-sm text-muted-foreground">No driver cycles recorded yet.</div>;
  }
  const totals = cycles.reduce(
    (acc, c) => ({
      gross: acc.gross + (c.gross_earnings || 0),
      fee: acc.fee + (c.fee_amount || 0),
      net: acc.net + (c.net_earnings || 0),
    }),
    { gross: 0, fee: 0, net: 0 }
  );
  return (
    <div data-testid="cycles-table">
      <div className="grid grid-cols-3 gap-px bg-border border border-border mb-3">
        <div className="bg-white p-3">
          <div className="label-mini text-muted-foreground">TOTAL GROSS</div>
          <div className="font-heading text-lg font-black mt-1">{mask(`£${totals.gross.toFixed(2)}`)}</div>
        </div>
        <div className="bg-white p-3">
          <div className="label-mini text-muted-foreground">FEES COLLECTED</div>
          <div className="font-heading text-lg font-black mt-1 text-[#002FA7]">{mask(`£${totals.fee.toFixed(2)}`)}</div>
        </div>
        <div className="bg-[#FAFAFB] p-3">
          <div className="label-mini text-muted-foreground">DRIVER NET</div>
          <div className={`font-heading text-lg font-black mt-1 ${totals.net >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
            {mask(`£${totals.net.toFixed(2)}`)}
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        {cycles.slice(0, 100).map((c) => (
          <div key={`${c.driver_id}-${c.cycle_date}`} className="border border-border px-3 py-2 flex items-center justify-between text-xs" data-testid={`cycle-row-${c.driver_id}-${c.cycle_date}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{c.driver_name}</div>
              <div className="label-mini text-muted-foreground font-mono-tab">{c.cycle_date} · {c.vehicle_plate || "—"}</div>
            </div>
            <div className="flex items-center gap-3 font-mono-tab flex-shrink-0">
              <span>{mask(`£${(c.gross_earnings ?? 0).toFixed(2)}`)}</span>
              <span className="text-[#EF4444]">{mask(`−£${(c.fee_amount ?? 0).toFixed(2)}`)}</span>
              <span className={`font-bold ${(c.net_earnings ?? 0) >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                {mask(`£${(c.net_earnings ?? 0).toFixed(2)}`)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
